import { useState, useEffect } from 'react'
import {
  getUnidades,
  createUnidade,
  updateUnidade,
  deleteUnidade,
  type Unidade,
} from '@/api/adminApi'
import { AdminPage, AdminPanel } from '@/components/Admin'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import styles from './Admin.module.css'

const FORM_EMPTY = { nome: '', morada: '', mapa_url: '', ativo: true, ordem: 0 }

export function AdminUnidadesPage() {
  const [list, setList] = useState<Unidade[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<Unidade | null>(null)
  const [form, setForm] = useState(FORM_EMPTY)
  const [saving, setSaving] = useState(false)
  const { notifySaved, notifyDeleted, notifyError, notifyValidation } = useSaveFeedback()

  const load = () => {
    setError(null)
    getUnidades(true)
      .then(setList)
      .catch((e) => setError(e instanceof Error ? e.message : 'Erro ao carregar'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [])

  const resetForm = () => {
    setEditing(null)
    setForm(FORM_EMPTY)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.nome.trim()) {
      notifyValidation('Indique o nome da unidade.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      if (editing) {
        await updateUnidade(editing.id, {
          nome: form.nome,
          morada: form.morada || undefined,
          mapa_url: form.mapa_url || undefined,
          ativo: form.ativo,
          ordem: form.ordem,
        })
        notifySaved('Unidade actualizada.')
      } else {
        await createUnidade({
          nome: form.nome,
          morada: form.morada || undefined,
          mapa_url: form.mapa_url || undefined,
          ativo: form.ativo,
          ordem: form.ordem,
        })
        notifySaved('Unidade criada.')
      }
      resetForm()
      load()
    } catch (e) {
      notifyError(e, 'Erro ao salvar')
      setError(e instanceof Error ? e.message : 'Erro ao salvar')
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (u: Unidade) => {
    setEditing(u)
    setForm({
      nome: u.nome,
      morada: u.morada ?? '',
      mapa_url: u.mapa_url ?? '',
      ativo: !!u.ativo,
      ordem: u.ordem ?? 0,
    })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Excluir esta unidade?')) return
    setError(null)
    try {
      await deleteUnidade(id)
      if (editing?.id === id) resetForm()
      load()
      notifyDeleted('Unidade removida.')
    } catch (e) {
      notifyError(e, 'Erro ao excluir')
      setError(e instanceof Error ? e.message : 'Erro ao excluir')
    }
  }

  return (
    <AdminPage
      title="Unidades"
      subtitle="Gerencie filiais com nome, morada e link do Google Maps para o app."
      loading={loading}
      error={error}
      wide
    >
      <AdminPanel
        title={editing ? `Editar — ${editing.nome}` : 'Nova unidade'}
        footer={
          <div className={styles.formActions} style={{ margin: 0 }}>
            <button type="submit" className={styles.btnSalvar} disabled={saving} form="form-unidade">
              {saving ? 'A guardar…' : editing ? 'Guardar alterações' : 'Adicionar unidade'}
            </button>
            {editing && (
              <button type="button" className={styles.btnCancel} onClick={resetForm}>
                Cancelar
              </button>
            )}
          </div>
        }
      >
        <form id="form-unidade" onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="un-nome">Nome *</label>
            <input
              id="un-nome"
              value={form.nome}
              onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
              placeholder="Nome da unidade"
              required
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="un-morada">Morada</label>
            <input
              id="un-morada"
              value={form.morada}
              onChange={(e) => setForm((f) => ({ ...f, morada: e.target.value }))}
              placeholder="Endereço"
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="un-mapa">URL do mapa (Google Maps)</label>
            <input
              id="un-mapa"
              type="url"
              value={form.mapa_url}
              onChange={(e) => setForm((f) => ({ ...f, mapa_url: e.target.value }))}
              placeholder="https://..."
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="un-ordem">Ordem</label>
            <input
              id="un-ordem"
              type="number"
              min={0}
              value={form.ordem}
              onChange={(e) => setForm((f) => ({ ...f, ordem: Number(e.target.value) || 0 }))}
            />
          </div>
          <label className={styles.inlineCheck} htmlFor="unidade-ativo">
            <input
              id="unidade-ativo"
              type="checkbox"
              checked={form.ativo}
              onChange={(e) => setForm((f) => ({ ...f, ativo: e.target.checked }))}
            />
            Unidade activa no app
          </label>
        </form>
      </AdminPanel>

      <AdminPanel title={`Unidades registadas (${list.length})`}>
        {list.length === 0 ? (
          <p className={styles.listEmpty}>Nenhuma unidade. Adicione a primeira acima.</p>
        ) : (
          <div className={styles.listScrollArea}>
            <ul className={styles.list}>
            {list.map((u) => (
              <li key={u.id} className={styles.listItem}>
                <div className={styles.listItemContent}>
                  <div className={styles.listItemTitle}>{u.nome}</div>
                  <p className={styles.listItemMeta}>{u.morada || 'Sem morada'}</p>
                  {!u.ativo && <p className={styles.listItemMeta}>Inactiva</p>}
                </div>
                <div className={styles.listItemActions}>
                  <button type="button" onClick={() => handleEdit(u)} className={styles.btnSecondary}>
                    Editar
                  </button>
                  <button type="button" onClick={() => handleDelete(u.id)} className={styles.btnDanger}>
                    Excluir
                  </button>
                </div>
              </li>
            ))}
            </ul>
          </div>
        )}
      </AdminPanel>
    </AdminPage>
  )
}
